// Externals imports.
import React, { ReactElement, useMemo, useState } from 'react'
import { useSnackbar } from '../../../../Vendor/notistack'

import { LanguageSnackbar } from '../../../../../i18n'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { messageType, ReactState } from '../../Types/Types'
// Commons imports.
import Icon, { IconName } from '../Icon'
// Sub imports.
import { DisplayType } from './Constants'
import MoreDetailsType from './Types'
// CSS imports. Commented out because of current build process.
// import './snackbarMessage.scss';

/**
 * Component properties type.
 */
type Props = {
    displayType: number
    snackElementReference: string | number
    message: messageType
    details?: MoreDetailsType
}

/**
 * Message properties type.
 */
type MessageProperties = {
    tileClassName: string
    icon: ReactElement
}

/**
 * Generic Snackbar component.
 * @param displayType Name of the snackbar type wanting to be rendered.
 * @param snackElementReference Snack element key needed for internal implementation.
 * @param message The snack message.
 * @param details If this exists it will have a expansion option for more details.
 */
const SnackbarMessage: React.FC<Props> = ({
    displayType,
    snackElementReference,
    message,
    details
}: Props): ReactElement => {
    // Access language resources directly without context as this is top level component.
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle('LanguageSnackbar')

    const [moreDetailOpen, setMoreDetailOpen]: ReactState<boolean> = useState<boolean>(false)

    const { closeSnackbar } = useSnackbar()

    const { tileClassName, icon }: MessageProperties = useMemo(() => {
        switch (displayType) {
            case DisplayType.Error:
                return {
                    tileClassName: 'snackbar-wrapper error-tile',
                    icon: <Icon iconName={IconName.ERRORCIRCLE} />
                }

            case DisplayType.Success:
                return {
                    tileClassName: 'snackbar-wrapper success-tile',
                    icon: <Icon iconName={IconName.SUCCESS} />
                }

            case DisplayType.Feedback:
                return {
                    tileClassName: 'snackbar-wrapper feedback-tile',
                    icon: <Icon iconName={IconName.FEEDBACK} />
                }

            case DisplayType.Warning:
                return {
                    tileClassName: 'snackbar-wrapper warning-tile',
                    icon: <Icon iconName={IconName.WARNING} />
                }

            case DisplayType.Information:
            default:
                return {
                    tileClassName: 'snackbar-wrapper info-tile',
                    icon: <Icon iconName={IconName.INFO} />
                }
        }
    }, [message, snackElementReference, displayType])

    const detailToggle = (): void => {
        setMoreDetailOpen(!moreDetailOpen)
    }

    return (
        <div className={tileClassName}>
            <div className='snackbar-close' onClick={(): void => closeSnackbar(snackElementReference)}>
                <Icon iconName={IconName.X} />
            </div>
            {icon}
            <div>{message}</div>
            {!!details && (
                <>
                    <div className={`more-content ${moreDetailOpen ? 'active' : ''}`}>{details}</div>
                    <button type='button' onClick={detailToggle} className='menu-toggle'>
                        {moreDetailOpen ? (
                            <>
                                <p>{language.get(LanguageSnackbar.ShowLess)}</p>
                                <Icon iconName={IconName.ARROW_UP} />
                            </>
                        ) : (
                            <>
                                <p>{language.get(LanguageSnackbar.ShowMore)}</p>
                                <Icon iconName={IconName.ARROW_DOWN} />
                            </>
                        )}
                    </button>
                </>
            )}
        </div>
    )
}

export default SnackbarMessage
